import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Users, Bed, Bath, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import BookingModal from './BookingModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROOM_TYPE_LABEL = {
  couple: 'Couple Room',
  deluxe: 'Deluxe Room',
  standard: 'Standard Room',
};

const RoomCard = ({ room, index, onBook }) => {
  const [imgIdx, setImgIdx] = useState(0);
  const images = room.images && room.images.length > 0 ? room.images : [room.image_url];

  const next = (e) => { e.stopPropagation(); setImgIdx((p) => (p + 1) % images.length); };
  const prev = (e) => { e.stopPropagation(); setImgIdx((p) => (p - 1 + images.length) % images.length); };

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.8, delay: (index % 3) * 0.15 }}
      className="group relative flex-shrink-0 w-[85vw] sm:w-[460px] md:w-[520px] snap-start"
      data-testid={`room-card-${room.id}`}
    >
      {/* image */}
      <div className="relative h-[420px] md:h-[520px] overflow-hidden bg-[#1A2421]">
        <img
          src={images[imgIdx]}
          alt={room.name}
          className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F0E] via-[#0B0F0E]/30 to-transparent" />

        {/* room number top-left */}
        <div className="absolute top-6 left-6">
          <div className="numeral text-5xl text-[#D4AF37]">{String(room.room_number ?? index + 1).padStart(2, '0')}</div>
        </div>

        {/* type badge top-right */}
        <div className="absolute top-6 right-6 price-tag">
          {ROOM_TYPE_LABEL[room.room_type] || room.room_type}
        </div>

        {/* image controls */}
        {images.length > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#0B0F0E]/70 border border-[#D4AF37]/30 text-[#D4AF37] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-[#D4AF37] hover:text-[#0B0F0E]"
              data-testid={`room-prev-img-${room.id}`}
              aria-label="Previous image"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={next}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#0B0F0E]/70 border border-[#D4AF37]/30 text-[#D4AF37] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-[#D4AF37] hover:text-[#0B0F0E]"
              data-testid={`room-next-img-${room.id}`}
              aria-label="Next image"
            >
              <ChevronRight size={16} />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
              {images.map((_, i) => (
                <div key={i} className={`h-px transition-all ${i === imgIdx ? 'w-8 bg-[#D4AF37]' : 'w-4 bg-[#E8E3D9]/30'}`} />
              ))}
            </div>
          </>
        )}

        {/* bottom name */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <h3 className="font-serif text-3xl md:text-4xl text-[#E8E3D9] font-light leading-tight">
            {room.name}
          </h3>
        </div>
      </div>

      {/* details panel — overlapping */}
      <div className="relative -mt-4 mx-4 md:mx-6 bg-[#0B0F0E] border border-[#D4AF37]/30 p-6 md:p-7">
        <p className="text-[13px] text-[#8E9A95] leading-relaxed mb-5 line-clamp-2">
          {room.description}
        </p>

        <div className="flex flex-wrap gap-x-5 gap-y-2 mb-6 text-[12px] text-[#C4BFB1]">
          {room.capacity > 0 && (
            <div className="flex items-center gap-1.5">
              <Users size={14} className="text-[#D4AF37]" />
              <span>{room.capacity}</span>
            </div>
          )}
          {room.beds && (
            <div className="flex items-center gap-1.5">
              <Bed size={14} className="text-[#D4AF37]" />
              <span>{room.beds}</span>
            </div>
          )}
          {room.washroom && (
            <div className="flex items-center gap-1.5">
              <Bath size={14} className="text-[#D4AF37]" />
              <span>{room.washroom}</span>
            </div>
          )}
        </div>

        <div className="flex items-end justify-between pt-5 border-t border-[#D4AF37]/15">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#8E9A95]">From</div>
            <div className="numeral text-3xl text-[#D4AF37] leading-none mt-1">
              ₹{room.price_per_head.toLocaleString()}
            </div>
            <div className="text-[10px] text-[#8E9A95] mt-1">per room / night + taxes</div>
          </div>
          <button
            onClick={() => onBook(room)}
            className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-medium text-[#D4AF37] hover:gap-3 transition-all"
            data-testid={`book-room-${room.id}`}
          >
            Reserve
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </motion.article>
  );
};

const RoomsSection = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const railRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const response = await axios.get(`${API_URL}/api/rooms`);
        setRooms(response.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleBook = (room) => {
    setSelectedRoom(room);
    setIsModalOpen(true);
  };

  const filters = [
    { value: 'all', label: 'All Rooms' },
    { value: 'deluxe', label: 'Deluxe' },
    { value: 'standard', label: 'Standard' },
    { value: 'couple', label: 'Couple' },
  ];

  const filteredRooms = filter === 'all' ? rooms : rooms.filter(r => r.room_type === filter);

  const counts = {
    deluxe: rooms.filter(r => r.room_type === 'deluxe').length,
    standard: rooms.filter(r => r.room_type === 'standard').length,
    couple: rooms.filter(r => r.room_type === 'couple').length,
  };

  const scrollRail = (dir) => {
    if (!railRef.current) return;
    const dx = railRef.current.clientWidth * 0.7;
    railRef.current.scrollBy({ left: dir === 'next' ? dx : -dx, behavior: 'smooth' });
  };

  return (
    <section id="rooms" className="relative bg-[#0B0F0E] py-32 md:py-44 overflow-hidden" data-testid="rooms-section">
      <div className="max-w-[1480px] mx-auto px-6 md:px-12">

        {/* chapter heading */}
        <div className="grid grid-cols-12 gap-6 mb-14 md:mb-20">
          <div className="col-span-12 lg:col-span-3">
            <div className="overline">Chapter II</div>
            <div className="numeral text-7xl md:text-8xl text-[#D4AF37]/30 mt-4">02</div>
          </div>
          <div className="col-span-12 lg:col-span-9">
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="font-serif text-4xl md:text-6xl lg:text-7xl font-light text-[#E8E3D9] leading-[1.05]"
            >
              Nine rooms.<br/>
              <span className="italic text-[#D4AF37]">No two alike.</span>
            </motion.h2>
            <p className="mt-6 max-w-xl text-[#8E9A95] text-[15px] leading-relaxed">
              Every room is a quiet conversation with the forest. Hand-finished interiors,
              attached washrooms, and three rate tiers — <span className="text-[#D4AF37]">Couple ₹5,500</span>,
              <span className="text-[#D4AF37]"> Standard ₹6,500</span> and
              <span className="text-[#D4AF37]"> Deluxe ₹7,500</span> per room/night on double occupancy.
              Bed and breakfast included.
            </p>
          </div>
        </div>

        {/* type counter */}
        <div className="grid grid-cols-3 max-w-2xl mb-14 border-y border-[#D4AF37]/15">
          {[
            { label: 'Deluxe', count: counts.deluxe, key: 'deluxe' },
            { label: 'Standard', count: counts.standard, key: 'standard' },
            { label: 'Couple', count: counts.couple, key: 'couple' },
          ].map((item, i) => (
            <button
              key={item.key}
              onClick={() => setFilter(item.key)}
              className={`text-left py-6 px-4 transition-colors ${i > 0 ? 'border-l border-[#D4AF37]/15' : ''} ${filter === item.key ? 'bg-[#D4AF37]/5' : ''}`}
              data-testid={`filter-${item.key}`}
            >
              <div className="numeral text-3xl md:text-4xl text-[#D4AF37]">
                {String(item.count).padStart(2, '0')}
              </div>
              <div className="overline text-[10px] mt-2" style={{ color: filter === item.key ? '#D4AF37' : '#8E9A95' }}>
                {item.label}
              </div>
            </button>
          ))}
        </div>

        {/* filter chips */}
        <div className="flex flex-wrap gap-2 mb-10">
          {filters.map(f => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-4 py-2 text-[11px] tracking-[0.2em] uppercase border transition-all ${
                filter === f.value
                  ? 'bg-[#D4AF37] text-[#0B0F0E] border-[#D4AF37]'
                  : 'border-[#D4AF37]/30 text-[#C4BFB1] hover:border-[#D4AF37] hover:text-[#D4AF37]'
              }`}
              data-testid={`chip-${f.value}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* rail controls */}
        <div className="flex items-center justify-between mb-6">
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#8E9A95]">
            {filteredRooms.length} {filteredRooms.length === 1 ? 'room' : 'rooms'} · scroll →
          </div>
          <div className="hidden md:flex gap-2">
            <button
              onClick={() => scrollRail('prev')}
              className="w-11 h-11 border border-[#D4AF37]/40 text-[#D4AF37] flex items-center justify-center hover:bg-[#D4AF37] hover:text-[#0B0F0E] transition-all"
              data-testid="rail-prev"
              aria-label="Scroll previous"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => scrollRail('next')}
              className="w-11 h-11 border border-[#D4AF37]/40 text-[#D4AF37] flex items-center justify-center hover:bg-[#D4AF37] hover:text-[#0B0F0E] transition-all"
              data-testid="rail-next"
              aria-label="Scroll next"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* horizontal snap rail — full bleed */}
      {loading ? (
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 flex gap-6">
          {[1, 2, 3].map(i => <div key={i} className="flex-shrink-0 w-[460px] h-[520px] bg-[#1A2421] animate-pulse" />)}
        </div>
      ) : (
        <div
          ref={railRef}
          className="snap-rail flex gap-6 md:gap-8 overflow-x-auto snap-x snap-mandatory pl-6 md:pl-12 pr-6 md:pr-12 pb-8"
          data-testid="rooms-rail"
        >
          {filteredRooms.map((room, idx) => (
            <RoomCard key={room.id} room={room} index={idx} onBook={handleBook} />
          ))}
          {/* tail spacer */}
          <div className="flex-shrink-0 w-2" />
        </div>
      )}

      {/* info strip */}
      <div className="max-w-[1480px] mx-auto px-6 md:px-12 mt-20 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#D4AF37]/15">
        {[
          { title: 'Check-in / Check-out', body: '1:00 PM — 11:00 AM' },
          { title: 'Bed & Breakfast', body: 'Breakfast included · à la carte dining' },
          { title: 'Group rates', body: 'Special discounts on group bookings' },
        ].map((item, i) => (
          <div key={i} className="bg-[#0B0F0E] p-8">
            <div className="overline mb-3">{item.title}</div>
            <p className="text-[#C4BFB1] text-sm">{item.body}</p>
          </div>
        ))}
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        room={selectedRoom}
      />
    </section>
  );
};

export default RoomsSection;
